import { useState, useRef, useEffect, KeyboardEvent } from "react"
import { Textarea } from "@/components/ui/textarea"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { supabase } from "@/integrations/supabase/client"
import { cn } from "@/lib/utils"

interface User {
  id: string
  full_name: string
  logo_url: string | null
}

interface MentionInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  minHeight?: string
}

export function MentionInput({
  value,
  onChange,
  placeholder,
  disabled,
  className,
  minHeight = "100px"
}: MentionInputProps) {
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [suggestions, setSuggestions] = useState<User[]>([])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [mentionQuery, setMentionQuery] = useState("")
  const [cursorPosition, setCursorPosition] = useState(0)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const suggestionsRef = useRef<HTMLDivElement>(null)

  // Search users when @ is typed
  useEffect(() => {
    const searchUsers = async () => {
      if (!mentionQuery || mentionQuery.length < 1) {
        setSuggestions([])
        return
      }

      const { data, error } = await supabase
        .from("public_profiles")
        .select("id, full_name, logo_url")
        .ilike("full_name", `%${mentionQuery}%`)
        .order("full_name", { ascending: true })
        .limit(10)

      if (!error && data) {
        setSuggestions(data.filter(u => u.full_name) as User[])
      }
    }

    const debounce = setTimeout(searchUsers, 200)
    return () => clearTimeout(debounce)
  }, [mentionQuery])

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    const position = e.target.selectionStart || 0
    setCursorPosition(position)
    onChange(newValue)

    // Check if we're in a mention context (supports Unicode/accented characters)
    const textBeforeCursor = newValue.substring(0, position)
    const mentionMatch = textBeforeCursor.match(/@([\p{L}\p{N}_]*)$/u)

    if (mentionMatch) {
      setMentionQuery(mentionMatch[1])
      setShowSuggestions(true)
      setSelectedIndex(0)
    } else {
      setShowSuggestions(false)
      setMentionQuery("")
    }
  }

  const insertMention = (user: User) => {
    const textBeforeCursor = value.substring(0, cursorPosition)
    const textAfterCursor = value.substring(cursorPosition)
    
    // Find where the @ symbol starts
    const mentionStart = textBeforeCursor.lastIndexOf("@")
    const newText = 
      textBeforeCursor.substring(0, mentionStart) + 
      `@[${user.full_name}] ` + 
      textAfterCursor

    onChange(newText)
    setShowSuggestions(false)
    setMentionQuery("")
    
    // Focus back on textarea
    setTimeout(() => {
      textareaRef.current?.focus()
    }, 0)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showSuggestions || suggestions.length === 0) return

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        )
        break
      case "ArrowUp":
        e.preventDefault()
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        )
        break
      case "Enter":
        if (showSuggestions && suggestions[selectedIndex]) {
          e.preventDefault()
          insertMention(suggestions[selectedIndex])
        }
        break
      case "Escape":
        e.preventDefault()
        setShowSuggestions(false)
        break
    }
  }

  return (
    <div className="relative w-full">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(`min-h-[${minHeight}] resize-none`, className)}
        style={{ minHeight }}
      />
      
      {showSuggestions && suggestions.length > 0 && (
        <div 
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-48 overflow-y-auto"
        >
          {suggestions.map((user, index) => (
            <button
              key={user.id}
              type="button"
              className={cn(
                "w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted transition-colors",
                index === selectedIndex && "bg-muted"
              )}
              onClick={() => insertMention(user)}
            >
              <Avatar className="h-6 w-6">
                <AvatarImage src={user.logo_url || undefined} />
                <AvatarFallback className="text-xs">
                  {user.full_name?.charAt(0) || "?"}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium">{user.full_name}</span>
            </button>
          ))}
        </div>
      )}
      
      <p className="text-xs text-muted-foreground mt-1">
        Digite @ para mencionar alguém
      </p>
    </div>
  )
}
